import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
} from '@mui/material';
import { Industry } from '../../models/Industry';
import { updateIndustry } from '../../services/industryService';

interface IndustryDetailsModalProps {
    open: boolean;
    industry: Industry | null;
    onClose: () => void;
    onSave: () => void;
}

export const IndustryDetailsModal: React.FC<IndustryDetailsModalProps> = ({
    open,
    industry,
    onClose,
    onSave,
}) => {
    const [editedIndustry, setEditedIndustry] = useState<Industry | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Initialize edited industry when modal opens with new industry
    React.useEffect(() => {
        setEditedIndustry(industry);
    }, [industry]);

    if (!editedIndustry) return null;

    const handleChange = (field: keyof Industry, value: any) => {
        setEditedIndustry(prev => {
            if (!prev) return prev;
            return { ...prev, [field]: value };
        });
    };

    const handleSave = async () => {
        if (!editedIndustry) return;

        try {
            setIsLoading(true);
            await updateIndustry(editedIndustry.id, editedIndustry);
            onSave();
            onClose();
        } catch (error) {
            console.error('Error updating industry:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeywordsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const keywords = event.target.value.split(',').map(k => k.trim());
        handleChange('keywords', keywords);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Edit Industry Details
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Name"
                        value={editedIndustry.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        fullWidth
                    />

                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={editedIndustry.status}
                            label="Status"
                            onChange={(e) => handleChange('status', e.target.value)}
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                            <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Keywords (comma-separated)"
                        value={editedIndustry.keywords?.join(', ')}
                        onChange={handleKeywordsChange}
                        fullWidth
                        helperText="Enter keywords separated by commas"
                    />

                    <TextField
                        label="Description"
                        value={editedIndustry.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                    />

                    {editedIndustry.keywords && editedIndustry.keywords.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {editedIndustry.keywords.map((keyword, index) => (
                                <Chip
                                    key={index}
                                    label={keyword}
                                    onDelete={() => {
                                        const newKeywords = editedIndustry.keywords?.filter((_, i) => i !== index);
                                        handleChange('keywords', newKeywords);
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 